import { createSelector, type ActionReducerMap } from '@ngrx/store';
import type { ArticlesPartialState } from '@candyland/angular/shared/article-tt-com/data-access';
import {
  reducer as articlesReducer,
  ArticlesEffects,
  ArticlesPreviewEffects,
} from '@candyland/angular/shared/article-tt-com/data-access';
import { routerReducer } from '@ngrx/router-store';
import type {
  AuthPartialStateV2,
  RouterPartialState,
  AdsPartialState,
} from '@candyland/angular/shared/data-access-nmo';
import {
  AuthV2Effects,
  WindowEffects,
  adsReducer,
  combinedAdEffects,
  GoogleAnalyticsEffects,
  authReducerV2,
} from '@candyland/angular/shared/data-access-nmo';
import { GoogleAnalyticsAppEffects } from './tracking/google-analytics/google-analytics.app.effects';
import type { OverviewPartialState } from '@candyland/angular/tt-com-www/overview/data-access';
import { overviewReducer, OverviewsEffects } from '@candyland/angular/tt-com-www/overview/data-access';
import { OewaAppEffects } from './tracking/oewa/oewa.app.effects';
import { MetaDataEffects } from '@candyland/angular/shared/apps-that-use-infomaker/data-access-meta-data';
import { AdsAppEffects } from './ads/ads.app.effects';
import { PageChangeAppEffects } from './page-change/page-change.app.effects';
import { MocafirstAppEffects } from './tracking/mocafirst/mocafirst.app.effects';
import { KilkayaAppEffects } from './tracking/kilkaya/kilkaya.app.effects';
import { ShowcaseAppEffects } from './showcase/showcase.app.effects';
import { PianoAppEffects } from './piano/piano.app.effects';
import type { PianoPartialState } from './piano/piano.app.reducer';
import { pianoReducer } from './piano/piano.app.reducer';
import type { CommentUser } from '@candyland/generic/tt-com-comments/models';
import { COMMENTS_FEATURE_KEY, commentUserDetailsReducer } from './comments/comments.reducer';
import type { CommentUserDetailsPartialState } from './comments/comments.reducer';

export type AppState = ArticlesPartialState &
  RouterPartialState &
  AuthPartialStateV2 &
  OverviewPartialState &
  AdsPartialState &
  PianoPartialState &
  CommentUserDetailsPartialState;

export const reducersV2: ActionReducerMap<AppState> = {
  articles: articlesReducer,
  router: routerReducer,
  auth: authReducerV2,
  overviews: overviewReducer,
  ads: adsReducer,
  piano: pianoReducer,
  commentUserDetails: commentUserDetailsReducer,
};

export const effectsV2 = [
  ArticlesEffects,
  ArticlesPreviewEffects,
  AuthV2Effects,
  WindowEffects,
  OverviewsEffects,
  GoogleAnalyticsEffects,
  GoogleAnalyticsAppEffects,
  OewaAppEffects,
  MocafirstAppEffects,
  KilkayaAppEffects,
  MetaDataEffects,
  AdsAppEffects,
  PageChangeAppEffects,
  ShowcaseAppEffects,
  ...combinedAdEffects,
  PianoAppEffects,
];

export const selectCommentUserDetails = createSelector(
  (state: AppState) => state[COMMENTS_FEATURE_KEY],
  (state: CommentUser) => state
);

export * from './router/page-views';
export * from './ads/ads.app.model';
export type { PianoPartialState } from './piano/piano.app.reducer';
export * from './piano/piano.app.actions';
export * from './piano/piano.app.service';
export * from './piano/piano.app.callbacks.service';
export type { CommentUserDetailsPartialState } from './comments/comments.reducer';
export * from './comments/comments.actions';
export * from './tracking/google-analytics/google-analytics.app.actions';
